import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import { format } from 'date-fns';
import { useAuth } from '../context/AuthContext';
import {
  getUserListings, deleteListing, updateListingStatus, updateListing, toggleListingHidden,
  getProfile, updateProfile, uploadQrImage
} from '../lib/supabase';

// Returns a small thumbnail URL — uses Supabase transform for hosted images,
// falls back to original with lazy loading for external URLs
function thumbUrl(url, size) {
  if (!url) return null;
  if (url.includes('/storage/v1/object/public/')) {
    const sep = url.includes('?') ? '&' : '?';
    return url + sep + 'width=' + size + '&height=' + size + '&resize=cover';
  }
  return url;
}

const STATUS_LABELS = {
  available: 'Available',
  deposit_paid: 'Deposit Paid',
  sold: 'Sold',
};

const AUTO_HIDE_DAYS = 90;
const AUTO_HIDE_MS = AUTO_HIDE_DAYS * 24 * 60 * 60 * 1000;

function daysInfo(createdAt) {
  const ageMs = Date.now() - new Date(createdAt).getTime();
  const ageDays = Math.floor(ageMs / (24 * 60 * 60 * 1000));
  const left = AUTO_HIDE_DAYS - ageDays;
  return { ageDays, daysLeft: left };
}
const STATUS_COLORS = {
  available:    '#7A9E7E',
  deposit_paid: '#C9A84C',
  sold:         '#888',
};


function DashThumb({ src }) {
  const [loaded, setLoaded] = React.useState(false);
  return (
    <div style={{ position: 'relative', width: '100%', height: '100%' }}>
      {!loaded && <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(90deg,#f0ebe3 25%,#e8e0d5 50%,#f0ebe3 75%)', backgroundSize: '400px 100%', animation: 'shimmer 1.4s ease-in-out infinite', borderRadius: 'var(--r)' }} />}
      <img src={src} alt="" loading="lazy" decoding="async" onLoad={() => setLoaded(true)} style={{ width: '100%', height: '100%', objectFit: 'cover', opacity: loaded ? 1 : 0, transition: 'opacity .3s' }} />
    </div>
  );
}

function SummaryCard({ label, value, sub, color }) {
  return (
    <div style={{ background: 'white', border: '1px solid var(--border)', borderRadius: 'var(--r-lg)', padding: '1.25rem 1.5rem', borderLeft: `4px solid ${color}` }}>
      <div style={{ fontSize: '.78rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.5px', color: 'var(--brown)', marginBottom: '.3rem' }}>{label}</div>
      <div style={{ fontSize: '1.75rem', fontWeight: 700, fontFamily: 'var(--font-d)', color: 'var(--ink)' }}>{value}</div>
      {sub && <div style={{ fontSize: '.82rem', color: 'var(--brown)', marginTop: '.2rem' }}>{sub}</div>}
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [activeTab, setActiveTab] = useState(searchParams.get('tab') === 'profile' ? 'profile' : 'listings');
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);

  const [profile, setProfile] = useState(null);
  const [qrFile, setQrFile] = useState(null);
  const [qrPreview, setQrPreview] = useState(null);
  const [qrUrl, setQrUrl] = useState('');
  const [telegram, setTelegram] = useState('');
  const [saving, setSaving] = useState(false);
  const [editingPrice, setEditingPrice] = useState(null); // { id, value }
  const [priceError, setPriceError] = useState('');
  const [editingDeposit, setEditingDeposit] = useState(null); // { id, value }
  const [depositError, setDepositError] = useState('');
  const [profileMsg, setProfileMsg] = useState('');
  const [selected, setSelected] = useState(new Set());
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!user) { navigate('/login'); return; }
    loadListings();
    loadProfile();
  }, [user]); // eslint-disable-line

  const loadListings = async () => {
    try {
      let data = await getUserListings(user.id) || [];
      // Auto-hide listings older than 90 days
      const toHide = data.filter(l => !l.hidden && (Date.now() - new Date(l.created_at).getTime()) >= AUTO_HIDE_MS);
      if (toHide.length > 0) {
        await Promise.all(toHide.map(l => toggleListingHidden(l.id, true)));
        data = data.map(l => toHide.find(h => h.id === l.id) ? { ...l, hidden: true } : l);
      }
      setListings(data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const loadProfile = async () => {
    try {
      const p = await getProfile(user.id);
      setProfile(p);
      setQrUrl(p?.payment_qr_url || '');
      setTelegram(p?.telegram_number || '');
    } catch (e) {}
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this listing?')) return;
    try {
      await deleteListing(id);
      setListings(prev => prev.filter(l => l.id !== id));
    } catch (e) { alert(e.message); }
  };

  const toggleSelect = (id) => {
    setSelected(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selected.size === listings.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(listings.map(l => l.id)));
    }
  };

  const handleBulkDelete = async () => {
    if (!window.confirm(`Delete ${selected.size} listing${selected.size !== 1 ? 's' : ''}? This cannot be undone.`)) return;
    setBulkDeleting(true);
    try {
      await Promise.all([...selected].map(id => deleteListing(id)));
      setListings(prev => prev.filter(l => !selected.has(l.id)));
      setSelected(new Set());
    } catch (e) { alert(e.message); }
    finally { setBulkDeleting(false); }
  };

  const handleToggleHidden = async (id, currentHidden) => {
    try {
      await toggleListingHidden(id, !currentHidden);
      setListings(prev => prev.map(l => l.id === id ? { ...l, hidden: !currentHidden } : l));
    } catch (e) { alert(e.message); }
  };

  const handleStatus = async (id, status) => {
    try {
      await updateListingStatus(id, status);
      setListings(prev => prev.map(l => l.id === id ? { ...l, status } : l));
    } catch (e) { alert(e.message); }
  };

  const savePrice = async (id) => {
    const val = parseFloat(editingPrice.value);
    if (isNaN(val) || val < 0) { setPriceError('Invalid price'); return; }
    try {
      await updateListing(id, { price: val });
      setListings(prev => prev.map(l => l.id === id ? { ...l, price: val } : l));
      setEditingPrice(null);
      setPriceError('');
    } catch (e) { setPriceError(e.message); }
  };

  const saveDeposit = async (id) => {
    const val = editingDeposit.value === '' ? null : parseFloat(editingDeposit.value);
    if (val !== null && (isNaN(val) || val < 0)) { setDepositError('Invalid amount'); return; }
    try {
      await updateListing(id, { deposit_amount: val });
      setListings(prev => prev.map(l => l.id === id ? { ...l, deposit_amount: val } : l));
      setEditingDeposit(null);
      setDepositError('');
    } catch (e) { setDepositError(e.message); }
  };

  const handleQrFile = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setQrFile(file);
    setQrPreview(URL.createObjectURL(file));
    setQrUrl('');
  };

  const saveProfile = async (e) => {
    e.preventDefault();
    setSaving(true);
    setProfileMsg('');
    try {
      let payment_qr_url = qrUrl.trim() || profile?.payment_qr_url || null;
      if (qrFile) payment_qr_url = await uploadQrImage(qrFile, user.id);
      await updateProfile(user.id, { payment_qr_url, telegram_number: telegram.trim() || null });
      setProfile(prev => ({ ...prev, payment_qr_url, telegram_number: telegram.trim() || null }));
      setQrUrl(payment_qr_url || '');
      setQrFile(null);
      setQrPreview(null);
      setProfileMsg('Profile saved!');
    } catch (e) {
      setProfileMsg('Error: ' + e.message);
    } finally {
      setSaving(false);
    }
  };

  const handleShare = () => {
    const url = `${window.location.origin}/seller/${profile?.username}`;
    navigator.clipboard.writeText(url).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  // ── SUMMARY CALCULATIONS ──────────────────────────
  const totalListings = listings.length;
  const totalValue = listings.reduce((s, l) => s + Number(l.price), 0);
  const soldValue = listings.filter(l => l.status === 'sold').reduce((s, l) => s + Number(l.price), 0);
  const depositItems = listings.filter(l => l.status === 'deposit_paid');
  const totalDeposits = depositItems.reduce((s, l) => s + (Number(l.deposit_amount) || 0), 0);
  const hasDeposits = depositItems.length > 0;
  const byCat = Object.entries(STATUS_LABELS).map(([status, label]) => {
    const items = listings.filter(l => l.status === status);
    const val = items.reduce((s, l) => s + Number(l.price), 0);
    return { status, label, count: items.length, val };
  }).filter(c => c.count > 0);

  return (
    <div className="page">
      <div className="page-header">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '1rem' }}>
          <div>
            <h1 className="page-title">Dashboard</h1>
            <p className="page-sub">Manage your listings and payment settings.</p>
          </div>
          {profile?.username && (
            <button
              className="btn btn-g"
              onClick={handleShare}
              style={{ display: 'flex', alignItems: 'center', gap: '.45rem', flexShrink: 0 }}
            >
              {copied ? '✓ Link copied!' : '🔗 Share my page'}
            </button>
          )}
        </div>
      </div>

      <div className="tabs">
        <div className={`tab ${activeTab === 'listings' ? 'on' : ''}`} onClick={() => setActiveTab('listings')}>
          My Listings ({listings.length})
        </div>
        <div className={`tab ${activeTab === 'summary' ? 'on' : ''}`} onClick={() => setActiveTab('summary')}>
          Summary
        </div>
        <div className={`tab ${activeTab === 'profile' ? 'on' : ''}`} onClick={() => setActiveTab('profile')}>
          Profile & Payment
        </div>
      </div>

      {/* ── LISTINGS TAB ── */}
      {activeTab === 'listings' && (
        <>
          <div style={{ marginBottom: '1rem', display: 'flex', gap: '.75rem', flexWrap: 'wrap', alignItems: 'center' }}>
            <button className="btn btn-p" onClick={() => navigate('/sell')}>+ New Listing</button>
            <button className="btn btn-g" onClick={() => navigate('/bulk-upload')}>📊 Bulk Upload</button>
          </div>

          <div className="alert alert-i" style={{ marginBottom: '1rem', fontSize: '.85rem', display: 'flex', gap: '.5rem', alignItems: 'flex-start' }}>
            <span>⏱</span>
            <span>Listings are automatically hidden after <strong>{AUTO_HIDE_DAYS} days</strong> to keep the marketplace fresh. You can reactivate any hidden listing at any time by clicking <strong>Unhide</strong>.</span>
          </div>

          {/* Bulk action toolbar — appears when items exist */}
          {listings.length > 0 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', padding: '.65rem 1rem', background: selected.size > 0 ? '#FFF2EF' : 'var(--cream)', border: '1px solid', borderColor: selected.size > 0 ? 'var(--terracotta)' : 'var(--border)', borderRadius: 'var(--r)', marginBottom: '1rem', flexWrap: 'wrap', transition: 'all .2s' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '.5rem', cursor: 'pointer', fontSize: '.875rem', fontWeight: 600, userSelect: 'none' }}>
                <input
                  type="checkbox"
                  checked={selected.size === listings.length && listings.length > 0}
                  ref={el => { if (el) el.indeterminate = selected.size > 0 && selected.size < listings.length; }}
                  onChange={toggleSelectAll}
                  style={{ width: 16, height: 16, cursor: 'pointer', accentColor: 'var(--terracotta)' }}
                />
                {selected.size === 0 ? 'Select all' : `${selected.size} of ${listings.length} selected`}
              </label>

              {selected.size > 0 && (
                <>
                  <div style={{ flex: 1 }} />
                  <button
                    className="btn btn-d btn-sm"
                    onClick={handleBulkDelete}
                    disabled={bulkDeleting}
                  >
                    {bulkDeleting ? 'Deleting…' : `🗑 Delete ${selected.size} listing${selected.size !== 1 ? 's' : ''}`}
                  </button>
                  <button className="btn btn-g btn-sm" onClick={() => setSelected(new Set())}>
                    Cancel
                  </button>
                </>
              )}
            </div>
          )}

          {loading ? (
            <div className="spinner-wrap"><div className="spinner" /></div>
          ) : listings.length === 0 ? (
            <div className="empty">
              <div className="empty-icon">📦</div>
              <h3>No listings yet</h3>
              <p style={{ marginBottom: '1.25rem' }}>Post your first item and start selling.</p>
              <button className="btn btn-p" onClick={() => navigate('/sell')}>List an Item</button>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              {listings.map(l => {
                const offerCount = l.queue_entries?.[0]?.count || 0;
                const isSelected = selected.has(l.id);
                return (
                  <div key={l.id} className='dash-listing-row' style={{ background: isSelected ? '#FFF2EF' : 'white', border: '1px solid', borderColor: isSelected ? 'var(--terracotta)' : 'var(--border)', borderRadius: 'var(--r-lg)', padding: '1.25rem', display: 'flex', gap: '1rem', alignItems: 'flex-start', transition: 'all .15s' }}>
                    {/* Checkbox */}
                    <div style={{ display: 'flex', alignItems: 'flex-start', paddingTop: '.25rem', flexShrink: 0 }}>
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => toggleSelect(l.id)}
                        style={{ width: 16, height: 16, cursor: 'pointer', accentColor: 'var(--terracotta)' }}
                      />
                    </div>
                    <div className='dash-listing-thumb' style={{ width: 80, height: 80, flexShrink: 0, borderRadius: 'var(--r)', overflow: 'hidden', background: 'var(--cream)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.8rem', border: '1px solid var(--border)' }}>
                      {l.image_url
                        ? <DashThumb src={thumbUrl(l.image_url, 120)} />
                        : '🛍️'}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '.5rem', flexWrap: 'wrap' }}>
                        <div>
                          <div style={{ fontFamily: 'var(--font-d)', fontWeight: 700, fontSize: '1.05rem' }}>{l.title}</div>
                          {/* Inline price edit */}
                          {editingPrice?.id === l.id ? (
                            <div style={{ display: 'flex', gap: '.4rem', alignItems: 'center', marginTop: '.2rem' }}>
                              <span style={{ color: 'var(--brown)', fontSize: '.85rem' }}>$</span>
                              <input
                                type="number" min="0" step="0.01"
                                value={editingPrice.value}
                                onChange={e => setEditingPrice(p => ({ ...p, value: e.target.value }))}
                                style={{ width: 90, padding: '.3rem .5rem', border: '1.5px solid var(--terracotta)', borderRadius: 'var(--r)', fontSize: '.95rem', fontWeight: 600 }}
                                autoFocus
                                onKeyDown={e => { if (e.key === 'Enter') savePrice(l.id); if (e.key === 'Escape') setEditingPrice(null); }}
                              />
                              <button className="btn btn-p btn-sm" onClick={() => savePrice(l.id)}>Save</button>
                              <button className="btn btn-g btn-sm" onClick={() => setEditingPrice(null)}>✕</button>
                              {priceError && <span style={{ fontSize: '.75rem', color: '#C62828' }}>{priceError}</span>}
                            </div>
                          ) : (
                            <div style={{ display: 'flex', alignItems: 'center', gap: '.5rem', marginTop: '.2rem' }}>
                              <span style={{ color: 'var(--terracotta)', fontWeight: 600, fontSize: '1rem' }}>${Number(l.price).toFixed(2)}</span>
                              <button
                                className="btn btn-g btn-sm"
                                style={{ padding: '.2rem .5rem', minHeight: 'auto', fontSize: '.72rem' }}
                                onClick={() => { setEditingPrice({ id: l.id, value: Number(l.price).toFixed(2) }); setPriceError(''); }}
                              >✏ Edit price</button>
                            </div>
                          )}
                          <div style={{ fontSize: '.78rem', color: 'var(--brown)', marginTop: '.25rem' }}>
                            Listed {format(new Date(l.created_at), 'MMM d, yyyy')}
                            {offerCount > 0 && <> · {offerCount} {offerCount === 1 ? 'offer' : 'offers'} to buy</>}
                            {(() => {
                              const { ageDays, daysLeft } = daysInfo(l.created_at);
                              if (l.hidden && ageDays >= AUTO_HIDE_DAYS) {
                                return <span style={{ marginLeft: '.5rem', color: '#888', fontStyle: 'italic' }}>· Auto-hidden after {AUTO_HIDE_DAYS} days</span>;
                              }
                              if (!l.hidden && daysLeft <= 7) {
                                return <span style={{ marginLeft: '.5rem', color: '#C62828', fontWeight: 600 }}>· Hides in {daysLeft} day{daysLeft !== 1 ? 's' : ''}</span>;
                              }
                              if (!l.hidden && daysLeft <= 30) {
                                return <span style={{ marginLeft: '.5rem', color: 'var(--gold)', fontWeight: 600 }}>· Hides in {daysLeft} days</span>;
                              }
                              return null;
                            })()}
                          </div>
                        </div>
                        <div style={{ display: 'flex', gap: '.4rem', alignItems: 'center', flexWrap: 'wrap' }}>
                          <span className={`badge b-${l.status}`}>{STATUS_LABELS[l.status]}</span>
                          {l.hidden && <span style={{ background: '#EEE', color: '#888', fontSize: '.68rem', fontWeight: 700, padding: '.15rem .55rem', borderRadius: 20, letterSpacing: '.4px', textTransform: 'uppercase' }}>Hidden</span>}
                        </div>
                      </div>
                      <div className="status-btns" style={{ marginTop: '.75rem' }}>
                        {Object.entries(STATUS_LABELS).map(([v, label]) => {
                          const active = l.status === v;
                          const c = STATUS_COLORS[v];
                          return (
                            <button key={v} className="s-btn" onClick={() => handleStatus(l.id, v)}
                              style={{ borderColor: c, color: active ? '#fff' : c, background: active ? c : 'transparent' }}>
                              {label}
                            </button>
                          );
                        })}
                      </div>

                      {/* Deposit amount — shown when status is deposit_paid */}
                      {l.status === 'deposit_paid' && (
                        <div style={{ marginTop: '.6rem', padding: '.5rem .75rem', background: '#FFFBEA', border: '1px solid #F0D060', borderRadius: 'var(--r)', display: 'inline-flex', alignItems: 'center', gap: '.5rem', flexWrap: 'wrap' }}>
                          {editingDeposit?.id === l.id ? (
                            <>
                              <span style={{ fontSize: '.82rem', color: 'var(--brown)', fontWeight: 600 }}>Deposit amount: $</span>
                              <input
                                type="number" min="0" step="0.01"
                                value={editingDeposit.value}
                                onChange={e => setEditingDeposit(p => ({ ...p, value: e.target.value }))}
                                style={{ width: 90, padding: '.3rem .5rem', border: '1.5px solid #C9A84C', borderRadius: 'var(--r)', fontSize: '.9rem', fontWeight: 600 }}
                                autoFocus
                                onKeyDown={e => { if (e.key === 'Enter') saveDeposit(l.id); if (e.key === 'Escape') setEditingDeposit(null); }}
                              />
                              <button className="btn btn-p btn-sm" onClick={() => saveDeposit(l.id)}>Save</button>
                              <button className="btn btn-g btn-sm" onClick={() => { setEditingDeposit(null); setDepositError(''); }}>✕</button>
                              {depositError && <span style={{ fontSize: '.75rem', color: '#C62828' }}>{depositError}</span>}
                            </>
                          ) : (
                            <>
                              <span style={{ fontSize: '.82rem', color: 'var(--brown)', fontWeight: 600 }}>Deposit received:</span>
                              <span style={{ fontSize: '.9rem', fontWeight: 700, color: '#8A6800' }}>
                                {l.deposit_amount != null ? `$${Number(l.deposit_amount).toFixed(2)}` : 'not entered'}
                              </span>
                              <button
                                className="btn btn-g btn-sm"
                                style={{ padding: '.2rem .5rem', minHeight: 'auto', fontSize: '.72rem' }}
                                onClick={() => { setEditingDeposit({ id: l.id, value: l.deposit_amount != null ? Number(l.deposit_amount).toFixed(2) : '' }); setDepositError(''); }}
                              >{l.deposit_amount != null ? '✏ Edit' : '+ Enter amount'}</button>
                            </>
                          )}
                        </div>
                      )}

                      <div style={{ display: 'flex', gap: '.5rem', marginTop: '.75rem', flexWrap: 'wrap' }}>
                        <button className="btn btn-g btn-sm" onClick={() => navigate(`/listing/${l.id}`)}>View</button>
                        <button className="btn btn-s btn-sm" onClick={() => navigate(`/listing/${l.id}/edit`)}>✏ Edit</button>
                        <button
                          className="btn btn-sm"
                          onClick={() => handleToggleHidden(l.id, l.hidden)}
                          style={{ background: l.hidden ? 'var(--sage)' : 'transparent', color: l.hidden ? '#fff' : 'var(--brown)', border: '1.5px solid', borderColor: l.hidden ? 'var(--sage)' : 'var(--border)' }}
                        >{l.hidden ? '👁 Unhide' : '🙈 Hide'}</button>
                        <button className="btn btn-d btn-sm" onClick={() => handleDelete(l.id)}>Delete</button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}

      {/* ── SUMMARY TAB ── */}
      {activeTab === 'summary' && (
        <div>
          {listings.length === 0 ? (
            <div className="empty">
              <div className="empty-icon">📊</div>
              <h3>No listings yet</h3>
              <p>Your summary will appear here once you add some listings.</p>
            </div>
          ) : (
            <>
              {/* Top stats */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '1rem', marginBottom: '2.5rem' }}>
                <SummaryCard label="Total Listings" value={totalListings} sub="items posted" color="var(--terracotta)" />
                <SummaryCard label="Total Value" value={`$${totalValue.toFixed(2)}`} sub="across all listings" color="var(--gold)" />
                <SummaryCard label="Total Sold" value={`$${soldValue.toFixed(2)}`} sub={`${listings.filter(l => l.status === 'sold').length} items sold`} color="var(--sage)" />
                {hasDeposits && (
                  <SummaryCard label="Deposits Received" value={`$${totalDeposits.toFixed(2)}`} sub={`${depositItems.length} item${depositItems.length !== 1 ? 's' : ''} with deposit`} color="#C9A84C" />
                )}
              </div>

              {/* By status breakdown */}
              <div className="section-title" style={{ marginBottom: '1rem' }}>Breakdown by Status</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '.75rem', marginBottom: '2.5rem' }}>
                {byCat.map(({ status, label, count, val }) => (
                  <div key={status} style={{ background: 'white', border: '1px solid var(--border)', borderRadius: 'var(--r-lg)', padding: '1rem 1.25rem', display: 'flex', alignItems: 'center', gap: '1rem' }}>
                    <span className={`badge b-${status}`} style={{ flexShrink: 0 }}>{label}</span>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 600, fontSize: '.95rem' }}>{count} {count === 1 ? 'item' : 'items'}</div>
                      <div style={{ fontSize: '.82rem', color: 'var(--brown)' }}>Total value: ${val.toFixed(2)}</div>
                    </div>
                    {/* Mini bar */}
                    <div style={{ width: 120, height: 8, background: 'var(--cream)', borderRadius: 4, overflow: 'hidden', flexShrink: 0 }}>
                      <div style={{ height: '100%', width: `${(count / totalListings) * 100}%`, background: STATUS_COLORS[status], borderRadius: 4, transition: 'width .4s' }} />
                    </div>
                    <div style={{ fontSize: '.82rem', color: 'var(--brown)', width: 36, textAlign: 'right', flexShrink: 0 }}>
                      {Math.round((count / totalListings) * 100)}%
                    </div>
                  </div>
                ))}
              </div>

              {/* All listings value table */}
              <div className="section-title" style={{ marginBottom: '1rem' }}>All Listings</div>
              <div style={{ border: '1px solid var(--border)', borderRadius: 'var(--r-lg)', overflow: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '.875rem', minWidth: 560 }}>
                  <thead>
                    <tr style={{ background: 'var(--cream)' }}>
                      {['Item', 'Category', 'Price', 'Deposit', 'Offers', 'Status', 'Listed'].map(h => (
                        <th key={h} style={{ padding: '.65rem 1rem', textAlign: 'left', fontWeight: 600, fontSize: '.75rem', textTransform: 'uppercase', letterSpacing: '.4px', color: 'var(--ink-light)', borderBottom: '1px solid var(--border)', whiteSpace: 'nowrap' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {listings.map((l, i) => (
                      <tr key={l.id}
                        onClick={() => navigate(`/listing/${l.id}`)}
                        style={{ borderBottom: '1px solid var(--border)', background: i % 2 === 0 ? 'white' : 'var(--warm-white)', cursor: 'pointer' }}
                      >
                        <td style={{ padding: '.6rem 1rem', fontWeight: 500 }}>{l.title}</td>
                        <td style={{ padding: '.6rem 1rem', color: 'var(--brown)', fontSize: '.82rem' }}>{l.category || '—'}</td>
                        <td style={{ padding: '.6rem 1rem', color: 'var(--terracotta)', fontWeight: 600, whiteSpace: 'nowrap' }}>${Number(l.price).toFixed(2)}</td>
                        <td style={{ padding: '.6rem 1rem', whiteSpace: 'nowrap' }}>
                          {l.status === 'deposit_paid'
                            ? <span style={{ color: '#8A6800', fontWeight: 600 }}>{l.deposit_amount != null ? `$${Number(l.deposit_amount).toFixed(2)}` : '—'}</span>
                            : <span style={{ color: 'var(--brown)' }}>—</span>
                          }
                        </td>
                        <td style={{ padding: '.6rem 1rem', textAlign: 'center' }}>
                          {(() => {
                            const cnt = l.queue_entries?.[0]?.count || 0;
                            if (cnt === 0) return <span style={{ color: 'var(--brown)' }}>—</span>;
                            return (
                              <span style={{ background: 'var(--terracotta)', color: '#fff', borderRadius: 20, padding: '.1rem .55rem', fontSize: '.78rem', fontWeight: 700 }}>
                                {cnt}
                              </span>
                            );
                          })()}
                        </td>
                        <td style={{ padding: '.6rem 1rem' }}><div style={{ display: 'flex', gap: '.4rem', alignItems: 'center', flexWrap: 'wrap' }}>
                          <span className={`badge b-${l.status}`}>{STATUS_LABELS[l.status]}</span>
                          {l.hidden && <span style={{ background: '#EEE', color: '#888', fontSize: '.68rem', fontWeight: 700, padding: '.15rem .55rem', borderRadius: 20, letterSpacing: '.4px', textTransform: 'uppercase' }}>Hidden</span>}
                        </div></td>
                        <td style={{ padding: '.6rem 1rem', color: 'var(--brown)', whiteSpace: 'nowrap' }}>{format(new Date(l.created_at), 'MMM d, yyyy')}</td>
                      </tr>
                    ))}
                    <tr style={{ background: 'var(--cream)', fontWeight: 700 }}>
                      <td style={{ padding: '.65rem 1rem' }}>TOTAL</td>
                      <td style={{ padding: '.65rem 1rem', color: 'var(--brown)', fontSize: '.82rem', fontWeight: 400 }}>{totalListings} listings</td>
                      <td style={{ padding: '.65rem 1rem', color: 'var(--terracotta)' }}>${totalValue.toFixed(2)}</td>
                      <td style={{ padding: '.65rem 1rem', color: '#8A6800' }}>{totalDeposits > 0 ? `$${totalDeposits.toFixed(2)}` : '—'}</td>
                      <td colSpan={3} style={{ padding: '.65rem 1rem' }} />
                    </tr>
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      )}

      {/* ── PROFILE TAB ── */}
      {activeTab === 'profile' && (
        <div className="form-wrap">
          <div className="form-card">
            <div className="form-head">
              <h2>Profile & Payment</h2>
              <p>Add your Telegram so buyers can contact you, and upload your payment QR so they can pay you directly.</p>
            </div>

            {profileMsg && (
              <div className={`alert ${profileMsg.startsWith('Error') ? 'alert-e' : 'alert-s'}`}>{profileMsg}</div>
            )}

            <form onSubmit={saveProfile}>

              {/* TELEGRAM */}
              <div className="fg">
                <label>Telegram Number or Username</label>
                <input
                  type="text"
                  value={telegram}
                  onChange={e => setTelegram(e.target.value)}
                  placeholder="@yourusername or +85512345678"
                />
                <div className="hint">
                  Buyers will see an "Ask the Seller on Telegram" button on your listings.
                  Enter your @username or phone number with country code.
                </div>
              </div>

              <div style={{ borderTop: '1px solid var(--border)', margin: '1.5rem 0' }} />

              {/* PAYMENT QR — current */}
              {(profile?.payment_qr_url && !qrFile) && (
                <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
                  <div style={{ fontSize: '.8rem', fontWeight: 600, color: 'var(--brown)', textTransform: 'uppercase', letterSpacing: '.5px', marginBottom: '.75rem' }}>
                    Current QR Code
                  </div>
                  <div style={{ display: 'inline-block', padding: '.75rem', background: 'white', border: '1px solid var(--border)', borderRadius: 8 }}>
                    <img src={profile.payment_qr_url} alt="Payment QR" style={{ width: 160, height: 160, objectFit: 'contain' }} />
                  </div>
                  <div style={{ fontSize: '.75rem', color: 'var(--sage)', marginTop: '.5rem' }}>✓ Your QR is live on your listings</div>
                </div>
              )}

              <div className="fg">
                <label>Payment QR Image <span style={{ fontWeight: 400, textTransform: 'none', letterSpacing: 0 }}>(optional)</span></label>
                <div className={`upload-area ${qrFile ? 'has' : ''}`} onClick={() => document.getElementById('qr-input').click()}>
                  {qrPreview
                    ? <img className="upload-preview" src={qrPreview} alt="QR Preview" />
                    : (
                      <div>
                        <div style={{ fontSize: '2rem', marginBottom: '.5rem' }}>📲</div>
                        <div style={{ fontSize: '.875rem', color: 'var(--brown)' }}>
                          {profile?.payment_qr_url ? 'Click to replace your QR code' : 'Click to upload your Bank QR Code'}
                        </div>
                      </div>
                    )
                  }
                </div>
                <input id="qr-input" type="file" accept="image/*" style={{ display: 'none' }} onChange={handleQrFile} />
              </div>

              <div className="divider"><span>or paste a payment link / bank details</span></div>

              <div className="fg">
                <label>Payment Link / URL</label>
                <input
                  type="url"
                  value={qrUrl}
                  onChange={e => { setQrUrl(e.target.value); setQrFile(null); setQrPreview(null); }}
                  placeholder="https://yourbank.com/pay/yourname"
                />
                <div className="hint">A QR code will be auto-generated from this link or URL.</div>
              </div>

              {qrUrl && !qrFile && (
                <div style={{ textAlign: 'center', marginBottom: '1rem' }}>
                  <div style={{ fontSize: '.78rem', color: 'var(--brown)', marginBottom: '.5rem' }}>QR Preview</div>
                  <div style={{ display: 'inline-block', padding: '.75rem', background: 'white', border: '1px solid var(--border)', borderRadius: 8 }}>
                    <QRCodeSVG value={qrUrl} size={140} />
                  </div>
                </div>
              )}

              <button className="btn btn-p btn-full" type="submit" disabled={saving}>
                {saving ? 'Saving…' : 'Save Profile'}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
